import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, X, BookOpen, Calculator, Atom } from '@phosphor-icons/react';
import { searchTopics } from '../data/topicIndex';

const SUBJECT_ICON = {
  maths: Calculator,
  english: BookOpen,
  science: Atom,
};

const SUBJECT_COLOR = {
  maths: 'text-indigo-500',
  english: 'text-rose-500',
  science: 'text-emerald-500',
};

/**
 * TopicSearch — compact search bar (desktop) that opens a results
 * dropdown. Also exposes an expanded version for mobile via the `fullWidth`
 * prop. Results navigate directly to the topic page on click.
 */
export default function TopicSearch({ placeholder = 'Search topics...', onNavigate = () => {}, fullWidth = false }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const results = useMemo(() => searchTopics(query, 10), [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const handleSelect = () => {
    setQuery('');
    setOpen(false);
    onNavigate();
  };

  return (
    <div ref={wrapperRef} className={`relative ${fullWidth ? 'w-full' : 'w-64'}`} data-testid="topic-search">
      <div className="relative">
        <MagnifyingGlass
          size={16}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="neo-input w-full !py-2 !pl-9 !pr-8 text-sm"
          data-testid="topic-search-input"
          aria-label="Search topics"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-secondary"
            aria-label="Clear search"
            data-testid="topic-search-clear"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {open && query && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-card border-2 border-foreground rounded-xl shadow-[4px_4px_0px_0px_rgb(var(--9ready-shadow))] max-h-96 overflow-y-auto z-50"
          data-testid="topic-search-dropdown"
        >
          {results.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground" data-testid="topic-search-empty">
              No topics match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <ul className="py-2">
              {results.map((r) => {
                const Icon = SUBJECT_ICON[r.subject] || BookOpen;
                const color = SUBJECT_COLOR[r.subject] || 'text-primary';
                return (
                  <li key={`${r.subject}-${r.topicId}`}>
                    <Link
                      to={r.href}
                      onClick={handleSelect}
                      className="flex items-start gap-3 px-3 py-2 hover:bg-secondary/60 transition-colors"
                      data-testid={`topic-search-result-${r.topicId}`}
                    >
                      <Icon size={18} weight="fill" className={`${color} mt-0.5 flex-shrink-0`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[10px] font-black uppercase tracking-wider ${color}`}>
                            {r.subjectLabel}
                          </span>
                          <span className="text-[10px] font-bold text-muted-foreground truncate">
                            · {r.sectionTitle}
                          </span>
                        </div>
                        <div className="font-bold text-sm truncate">{r.title}</div>
                        {r.summary && (
                          <div className="text-xs text-muted-foreground line-clamp-1">{r.summary}</div>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
